import React, {useCallback, useState} from 'react';
import {useLocation} from "react-router-dom";
import {CellProps, Column} from 'react-table';
import {ListTableWithSearchBox, SearchParams} from "components/pages/common/table/ListTableWithSearchBox";
import {LazyAndPreloadImage} from "components/pages/common/image/LazyAndPreloadImage";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFile} from "@fortawesome/free-solid-svg-icons";
import useAppRouter from "../../hooks/useAppRouter";
import {menuMaterialApi} from "../../apis/admin/menu/menuMaterialApi";
import {formatOmittedDateTime} from "../../libs/utils/formatDate";
import {MenuMaterialType} from "../../enum/menuMaterial/MenuMaterialType";
import { config } from '../../libs/config/config';
import {logError} from "../../libs/utils/logError";

interface MenuMaterial {
  id: number
  name: string
  materialTypeId: number,
  materialTypeName: string,
  content: string
  filename: string | null
  createdAt: string
  updatedAt: string
  mimeType: string | null
}

interface DataType extends MenuMaterial {
  operation?: string,
  icon?: string,
}

export const MenuMaterialListPage: React.FC = () => {
  const [menuMaterialList, setMenuMaterialList] = useState<DataType[]>([]);
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();
  const [totalCount, setTotalCount] = React.useState<number>(10);
  const { goto } = useAppRouter();
  const location = useLocation();

  const sortFunc = async (params: SearchParams) => {
    setAbortController(new AbortController());
    try
    {
      const offset = (params.currentPage - 1) * params.size;
      const response = await menuMaterialApi.index(params.keyword, params.size, offset, params.sort, { abortSignal: abortController?.signal });
      setAbortController(undefined);
      setMenuMaterialList(response.menuMaterialList);
      setTotalCount(response.totalHits);
    } catch(e) {
      await logError(
        location.pathname,
        'メニュー資料一覧の取得に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  }

  const initialSort = [
    'createdAt:desc'
  ];

  const handleCreate = async () => {
    goto('menuMaterialCreate');
  }

  // const [loaded, setLoaded] = useState(false);

  const IconCell = useCallback(({ row }: CellProps<DataType>) => {
    const { id, filename, mimeType, materialTypeId } = row.original;
    const extension = filename?.substring(filename?.lastIndexOf('.') + 1);
    const path = `${config.gcs.bucket.menuMaterial}/${id}.${extension}`;

    return (
      <>
        <div className="flex flex-col">
          {materialTypeId !== MenuMaterialType.URL && typeof mimeType === 'string' && mimeType.includes('image') && (
            <div className="flex items-center justify-center">
              <div className="w-16">
                <LazyAndPreloadImage
                  {...{path}}
                />
              </div>
            </div>
          )}
          {materialTypeId !== MenuMaterialType.URL && typeof mimeType === 'string' && !mimeType.includes('image') && (
            <div className="h-full w-full flex justify-center items-center">
              <FontAwesomeIcon
                icon={faFile}
                size="2x"
                className="text-gray-500"
              />
            </div>
          )}
        </div>
      </>
  )}, []);

  const DateTimeCell = useCallback(({ value }: CellProps<DataType>) => (
    <div>{ formatOmittedDateTime(value) }</div>
  ), []);

  const OperationCell = useCallback(({ row }: CellProps<DataType>) => {
    const { id, materialTypeId, filename } = row.original;
    const extension = filename?.substring(filename?.lastIndexOf('.') + 1);
    const path = `${config.gcs.bucket.menuMaterial}/${id}.${extension}`;

    const handleDownload = async () => {
      setAbortController(new AbortController());
      try {
        const response = await menuMaterialApi.downloadFile(
          { path },
          {
            abortSignal: abortController?.signal,
            responseType:'blob',
            dataType:'binary'
        });
        setAbortController(undefined);

        const downloadUrl = window.URL.createObjectURL(new Blob([response]));
        const downloadLink = document.createElement("a");
        document.body.appendChild(downloadLink);
        downloadLink.download = decodeURIComponent(filename!);
        downloadLink.href = downloadUrl;
        downloadLink.click();
        downloadLink.remove();
        URL.revokeObjectURL(downloadUrl);

      } catch(e) {
        goto('internalServerError');
      }
    }

    return (
      <div className="flex flex-col">
        <div>
          <button
            className="btn-primary mb-1"
            type='button'
            onClick={() => {
              goto('menuMaterialEdit', undefined, {id});
            }}
          >
            編 集
          </button>
        </div>
        {materialTypeId !== MenuMaterialType.URL && (
          <div>
              <input
                type="hidden"
                name="path"
                value={path}
              />
              <button
                className="btn-primary"
                type='button'
                onClick={handleDownload}
              >
                ダウンロード
              </button>
          </div>
        )}
      </div>
      // eslint-disable-next-line react-hooks/exhaustive-deps
  )}, [goto]);

  const columns: Column<DataType>[] = React.useMemo(
    () => [
      {
        Header: 'アイコン',
        accessor: 'icon',
        Cell: IconCell,
        disableSortBy: true,
      },
      {
        Header: '名称',
        accessor: 'name',
        className: 'select-none',
      },
      {
        Header: '説明',
        accessor: 'content',
        disableSortBy: true,
      },
      {
        Header: '資料タイプ',
        accessor: 'materialTypeName',
        disableSortBy: true,
      },
      {
        Header: '作成日時',
        accessor: 'createdAt',
        Cell: DateTimeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: OperationCell,
        disableSortBy: true,
      },
    ],
    [OperationCell, DateTimeCell,IconCell]
  );

  return (
    <>
      <div className="bg-sub min-h-full px-8">
        <div className="flex items-center py-6">
          <div className="text-xl text-primary font-semibold">メニュー資料</div>
          <div className="ml-auto">
            <button type="button"
                    className="btn-primary"
                    onClick={() => {
                      handleCreate();
                    }}
            >
              追 加
            </button>
          </div>
        </div>
        <ListTableWithSearchBox
          {...{
            tableData: menuMaterialList,
            columns,
            initialSort,
            totalCount,
            sortFunc,
          }}
        />
      </div>
    </>
  );
};
